import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../shared/i18n';
import { Footer, ResourcesSection } from '../shared/components';
import LinksSection from '../shared/components/LinksSection';
import { getVisibleGames } from '../shared/data/gamesCatalog';
import fortiGamerImage from '../assets/juega-con-forti/forti-gamer.png';
import * as S from './HomePage.styles';

function HomePage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isGamesSectionVisible, setIsGamesSectionVisible] = useState(false);
  const gamesSectionRef = useRef(null);
  const visibleGames = getVisibleGames();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsGamesSectionVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.3, // Se considera visible cuando al menos el 30% está en viewport
        rootMargin: '0px',
      }
    );

    const currentSection = gamesSectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const scrollToGames = () => {
    const gamesSection = document.getElementById('games-section');
    if (gamesSection) {
      gamesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFloatingButtonClick = () => {
    navigate('/juegaconforti');
  };

  return (
    <S.PageContainer>
      {/* Hero Section */}
      <S.HeroSection>
        <S.HeroBackground $offset={scrollY} />
        <S.HeroContent>
          <S.HeroTitle>{t('home.title')}</S.HeroTitle>
          <S.HeroSubtitle>
            {t('home.heroSubtitle')}
          </S.HeroSubtitle>
          <S.CTAButton onClick={scrollToGames}>
            {t('home.exploreGames')}
          </S.CTAButton>
        </S.HeroContent>
        <S.ScrollIndicator onClick={scrollToGames}>
          <S.ScrollArrow />
        </S.ScrollIndicator>
      </S.HeroSection>

      {/* Games Section */}
      <S.Section id="games-section" ref={gamesSectionRef} $background="white">
        <S.SectionTitle>{t('home.ourGames')}</S.SectionTitle>
        <S.SectionSubtitle>
          {t('home.subtitle')}
        </S.SectionSubtitle>

        <S.GamesGrid>
          {visibleGames.map((game) => {
            const IconComponent = game.badgeIcon;
            return (
              <S.GameCard key={game.id}>
                <S.GameImageContainer $bgImage={game.bgImage}>
                  <S.StatusBadge $status={game.status}>
                    {t(`games.status.${game.status}`)}
                  </S.StatusBadge>
                  <S.GameImage
                    src={game.logo}
                    alt={t(game.nameKey)}
                  />
                  {IconComponent && (
                    <S.GameBadge>
                      <IconComponent />
                    </S.GameBadge>
                  )}
                </S.GameImageContainer>

                <S.GameInfo>
                  <S.GameTitle>{t(game.nameKey)}</S.GameTitle>
                  <S.GameSubtitle>{t(game.subtitleKey)}</S.GameSubtitle>
                  <S.GameDescription>
                    {t(game.descriptionKey)}
                  </S.GameDescription>

                  <S.GameActionsContainer>
                    {game.actions.map((action) => (
                      <S.ActionButton
                        key={action.id}
                        as={action.enabled ? Link : 'button'}
                        to={action.enabled ? action.route : undefined}
                        disabled={!action.enabled}
                        $variant={action.variant}
                        onClick={(e) => {
                          if (!action.enabled) {
                            e.preventDefault();
                          }
                        }}
                      >
                        {t(action.labelKey)}
                      </S.ActionButton>
                    ))}
                  </S.GameActionsContainer>
                </S.GameInfo>
              </S.GameCard>
            );
          })}
        </S.GamesGrid>
      </S.Section>

      {/* Floating Action Button */}
      <S.FloatingButton
        $isVisible={isGamesSectionVisible}
        onClick={handleFloatingButtonClick}
        aria-label="Juega con Forti"
      >
        <S.FloatingButtonRipple2 />
        <S.FloatingButtonRipple />
        <S.ParticleContainer>
          {[...Array(6)].map((_, i) => (
            <S.Particle key={i} $delay={i * 0.3} $angle={i * 60} />
          ))}
        </S.ParticleContainer>
        <S.FloatingButtonImage src={fortiGamerImage} alt="Forti Gamer" />
      </S.FloatingButton>

      {/* Resources Section */}
      <S.ResourcesSectionWrapper>
        <S.SectionTitle>{t('resources.title')}</S.SectionTitle>
        <S.SectionSubtitle>
          {t('resources.subtitle')}
        </S.SectionSubtitle>

        <ResourcesSection />
      </S.ResourcesSectionWrapper>

      {/* Links Section */}
      <S.LinksSectionWrapper>
        <S.SectionTitle>{t('links.title')}</S.SectionTitle>
        <S.SectionSubtitle>
          {t('links.subtitle')}
        </S.SectionSubtitle>

        <LinksSection />
      </S.LinksSectionWrapper>

      {/* Footer */}
      <Footer />
    </S.PageContainer>
  );
}

export default HomePage; 